import { Emitter } from '../util/emitter';
import { ProgressTracker } from '../util/progress';
import { PeerConnection } from '../webrtc/peer';
import type { SignalingClient } from '../webrtc/signaling';
import {
  decodeChunk,
  decodeControl,
  encodeControl,
  type FileMetadata,
} from './protocol';
import {
  createDiskSink,
  DiskFileSink,
  MemoryFileSink,
  supportsFileSystemAccess,
  type FileSink,
} from './sink';
import {
  deleteCheckpoint,
  loadCheckpoint,
  saveCheckpoint,
  type ReceiverCheckpoint,
} from '../storage/idb';
import { compareHash, hashBlobStreaming } from './verify';
import type { ReceiverEvents } from './types';

const CHECKPOINT_EVERY_CHUNKS = 128;

/**
 * FileReceiver — drives the receiving side of a transfer.
 *
 * It adopts the DataChannel created by the sender, writes chunks straight to its
 * sink (disk stream or in-memory buffer), tracks contiguous progress, and on
 * `complete` re-reads the output to verify the SHA-256. Dropped connections are
 * recovered by asking the sender (via `resume`) for only the missing chunks; an
 * IndexedDB checkpoint additionally allows resuming a disk download across a
 * page reload.
 */
export class FileReceiver extends Emitter<ReceiverEvents> {
  private readonly peer: PeerConnection;

  private meta: FileMetadata | null = null;
  private sink: FileSink | null = null;
  private progress: ProgressTracker | null = null;
  private receivedContiguous = 0;
  private expectedHash = '';
  private completed = false;
  private chunksSinceCheckpoint = 0;
  private ticker: ReturnType<typeof setInterval> | null = null;
  // A stored checkpoint discovered at startup (reload-resume candidate).
  private pendingCheckpoint: ReceiverCheckpoint | null = null;

  constructor(
    private readonly signaling: SignalingClient,
    private readonly roomId: string,
  ) {
    super();
    this.peer = new PeerConnection(signaling, false);
    this.wire();
  }

  async start(): Promise<void> {
    this.emit('phase', 'connecting');
    // Look for a resumable disk download from a previous session.
    this.pendingCheckpoint = (await loadCheckpoint(this.roomId)) ?? null;
    if (this.pendingCheckpoint?.inMemory) {
      // In-memory progress can't survive a reload; discard it.
      await deleteCheckpoint(this.roomId);
      this.pendingCheckpoint = null;
    }
  }

  private wire(): void {
    this.signaling.on('peer-left', ({ role }) => {
      if (role === 'sender' && !this.completed) this.emit('phase', 'connecting');
    });

    this.peer.on('state', (s) => {
      if (s === 'connected' && !this.completed && this.meta) this.emit('phase', 'transferring');
    });

    this.peer.on('message', (data) => {
      if (typeof data === 'string') {
        this.onControl(data);
      } else {
        this.onChunk(data);
      }
    });

    this.peer.on('error', (err) => this.fail(err.message));
  }

  private onControl(raw: string): void {
    const msg = decodeControl(raw);
    if (!msg) return;
    if (msg.type === 'metadata') {
      void this.onMetadata(msg);
    } else if (msg.type === 'complete') {
      void this.onComplete(msg.sha256);
    } else if (msg.type === 'cancel') {
      this.fail('Transfer cancelled by sender', 'cancelled');
    }
  }

  private async onMetadata(meta: FileMetadata): Promise<void> {
    // Reconnect mid-transfer: sink already exists — just ask for what we lack.
    if (this.sink && this.meta) {
      this.requestResume();
      return;
    }

    this.meta = meta;
    this.expectedHash = '';
    this.progress = new ProgressTracker(meta.size, meta.totalChunks);
    this.emit('meta', meta);

    // The receiver must pick a destination via a user gesture (Feature 7).
    // Empty files flow through the same path: the sink finalizes to an empty
    // output and `complete` carries the hash of zero bytes.
    this.emit('phase', 'negotiating');
    this.emit('needsSaveLocation', meta);
  }

  /**
   * Called from a user gesture in the UI once the destination is chosen.
   * `mode` = 'disk' streams to a file via the File System Access API;
   * 'memory' buffers in RAM (fallback / small files).
   */
  async provideSaveLocation(mode: 'disk' | 'memory'): Promise<void> {
    if (!this.meta) throw new Error('metadata not received yet');
    if (this.sink) return; // already chosen

    if (mode === 'disk') {
      if (this.pendingCheckpoint?.fileHandle) {
        // Reload-resume: re-acquire permission on the existing partial file.
        const handle = this.pendingCheckpoint.fileHandle;
        const perm = await handle.requestPermission?.({ mode: 'readwrite' });
        if (perm === 'granted') {
          this.sink = await DiskFileSink.create(handle, true);
          this.receivedContiguous = this.pendingCheckpoint.receivedContiguous;
        }
      }
      if (!this.sink) {
        this.sink = await createDiskSink(this.meta);
      }
    } else {
      this.sink = new MemoryFileSink(this.meta);
    }

    if (this.progress) {
      const bytes = this.receivedContiguous * this.meta.chunkSize;
      this.progress.setProgress(Math.min(bytes, this.meta.size), this.receivedContiguous);
    }

    await this.persistCheckpoint(true);
    this.startTicker();
    this.emit('phase', 'transferring');
    this.requestResume();
  }

  private requestResume(): void {
    if (!this.meta) return;
    const missing = this.missingIndices();
    if (missing.length === this.meta.totalChunks || this.receivedContiguous === 0) {
      this.peer.send(encodeControl({ type: 'ready' }));
    } else {
      this.peer.send(encodeControl({ type: 'resume', missingChunks: missing }));
    }
  }

  private missingIndices(): number[] {
    if (!this.meta) return [];
    const out: number[] = [];
    for (let i = this.receivedContiguous; i < this.meta.totalChunks; i++) out.push(i);
    return out;
  }

  private onChunk(buffer: ArrayBuffer): void {
    if (!this.sink || !this.meta || !this.progress || this.completed) return;
    const { index, payload } = decodeChunk(buffer);

    // Ordered+reliable channel ⇒ chunks arrive in sequence. Ignore duplicates
    // (possible right after a resume) and only advance the contiguous counter.
    if (index < this.receivedContiguous) return;

    void this.writeChunk(index, payload);
  }

  private async writeChunk(index: number, payload: Uint8Array): Promise<void> {
    if (!this.sink || !this.meta || !this.progress) return;
    try {
      await this.sink.write(index, this.meta.chunkSize, payload);
      if (index === this.receivedContiguous) this.receivedContiguous = index + 1;
      this.progress.addChunk(payload.byteLength);

      if (++this.chunksSinceCheckpoint >= CHECKPOINT_EVERY_CHUNKS) {
        this.chunksSinceCheckpoint = 0;
        void this.persistCheckpoint(false);
      }
    } catch (err) {
      this.fail(`Failed to write chunk: ${(err as Error).message}`);
    }
  }

  private async onComplete(sha256: string): Promise<void> {
    if (!this.sink || !this.meta || this.completed) return;
    this.expectedHash = sha256;
    this.completed = true;
    this.stopTicker();
    this.emitProgress();
    this.emit('phase', 'verifying');

    try {
      const { objectUrl } = await this.sink.finalize();
      const blob = await this.sink.readBack();
      const actual = await hashBlobStreaming(blob);
      const integrity = compareHash(this.expectedHash, actual);
      await deleteCheckpoint(this.roomId);

      this.emit('phase', integrity === 'corrupted' ? 'error' : 'complete');
      this.emit('done', {
        sha256: actual,
        integrity,
        objectUrl,
        fileName: this.meta.name,
      });
    } catch (err) {
      this.fail(`Finalization failed: ${(err as Error).message}`);
    }
  }

  private async persistCheckpoint(force: boolean): Promise<void> {
    if (!this.meta || !this.sink) return;
    if (!force && this.completed) return;
    const handle = this.sink instanceof DiskFileSink ? this.sink.fileHandle : undefined;
    const cp: ReceiverCheckpoint = {
      roomId: this.roomId,
      meta: this.meta,
      receivedContiguous: this.receivedContiguous,
      fileHandle: handle,
      inMemory: !this.sink.streaming,
      updatedAt: Date.now(),
    };
    try {
      await saveCheckpoint(cp);
    } catch {
      /* checkpointing is best-effort */
    }
  }

  private startTicker(): void {
    if (this.ticker) return;
    this.emitProgress();
    this.ticker = setInterval(() => this.emitProgress(), 1000);
  }

  private stopTicker(): void {
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
  }

  private emitProgress(): void {
    if (this.progress) this.emit('progress', this.progress.snapshot());
  }

  private fail(message: string, phase: 'error' | 'cancelled' = 'error'): void {
    if (this.completed) return;
    this.stopTicker();
    this.emit('phase', phase);
    this.emit('error', { message });
  }

  /** Whether this browser can stream large downloads to disk. */
  get canStreamToDisk(): boolean {
    return supportsFileSystemAccess();
  }

  /** True when a previous disk download for this room can be resumed. */
  get hasResumableCheckpoint(): boolean {
    return this.pendingCheckpoint !== null;
  }

  cancel(): void {
    this.stopTicker();
    try {
      if (this.peer.dataChannel?.readyState === 'open') {
        this.peer.send(encodeControl({ type: 'cancel' }));
      }
    } catch {
      /* ignore */
    }
    void this.sink?.abort();
    this.emit('phase', 'cancelled');
  }

  destroy(): void {
    this.stopTicker();
    this.peer.close();
    this.removeAll();
  }
}
